import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, WifiOff, TrendingDown,
  TrendingUp, FileWarning, FileQuestion, Check, EyeOff, PlugZap, ListFilter,
} from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import TenantSelect from '../components/TenantSelect';

interface Source {
  id: number;
  name: string;
  site_name: string;
  tenant_name: string;
  last_seen_at: string | null;
  last_data_at: string | null;
  silent_minutes: string | null;
  offline_minutes: string | null;
  events_24h: number;
  unparsed_24h: number;
  latest_formats: Record<string, number> | null;
  open_issues: number;
}

interface Issue {
  id: number;
  broker_name: string | null;
  tenant_name: string;
  type: 'broker_offline' | 'no_data' | 'source_silent' | 'low_volume' | 'high_volume' | 'format_change' | 'parse_failures' | 'low_diversity';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  details: Record<string, unknown>;
  status: string;
  first_seen_at: string;
  last_seen_at: string;
}

const TYPE_ICON = {
  broker_offline: PlugZap,
  no_data: WifiOff,
  source_silent: WifiOff,
  low_volume: TrendingDown,
  high_volume: TrendingUp,
  format_change: FileWarning,
  parse_failures: FileQuestion,
  low_diversity: ListFilter,
};

const TYPE_LABEL: Record<Issue['type'], string> = {
  broker_offline: 'Broker offline',
  no_data: 'Devices not sending',
  source_silent: 'Device stopped sending',
  low_volume: 'Volume below trend',
  high_volume: 'Volume above trend',
  format_change: 'Log format changed',
  parse_failures: 'Unparsed logs',
  low_diversity: 'Few event types (check log categories)',
};

const SEVERITY_STYLE: Record<Issue['severity'], string> = {
  critical: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  info: 'bg-blue-50 border-blue-200 text-blue-700',
};

function formatSilence(minutes: string | null): string {
  if (minutes === null) return 'never';
  const value = Math.round(Number(minutes));
  if (value < 1) return 'just now';
  if (value < 60) return `${value} min ago`;
  const hours = Math.floor(value / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const SOURCE_COLUMNS: ColumnDef[] = [
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'tenant', label: 'Tenant', defaultVisible: true },
  { key: 'source', label: 'Source', defaultVisible: true },
  { key: 'site', label: 'Site', defaultVisible: true },
  { key: 'broker', label: 'Broker', defaultVisible: true },
  { key: 'last_data', label: 'Last data', defaultVisible: true },
  { key: 'events_24h', label: 'Events (24h)', defaultVisible: true },
  { key: 'formats', label: 'Formats', defaultVisible: true },
  { key: 'unparsed_24h', label: 'Unparsed (24h)', defaultVisible: true },
];

// Salute del flusso dati: mostra se ogni sorgente sta inviando, con che volumi rispetto al
// proprio andamento abituale, in che formato e quante righe non siamo riusciti a interpretare.
export default function DataQualityPage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.data_quality_sources', SOURCE_COLUMNS);
  const shownColumns = orderedDefs.filter(d => d.key !== 'tenant' || !selectedTenantId);
  const [sources, setSources] = useState<Source[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/data-quality', {
      params: { tenantId: selectedTenantId ?? undefined },
    });
    setSources(res.data.data.sources);
    setIssues(res.data.data.issues);
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  async function runChecks() {
    setRunning(true);
    try {
      await axios.post('/api/data-quality/run');
      await load();
    } finally {
      setRunning(false);
    }
  }

  async function updateIssue(id: number, status: 'acknowledged' | 'resolved') {
    await axios.put(`/api/data-quality/issues/${id}`, { status });
    load();
  }

  const healthy = sources.filter(s => s.open_issues === 0).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Data Quality</h1>
          <TenantSelect />
        </div>
        <button onClick={runChecks} disabled={running} className="btn-secondary flex items-center gap-1">
          <RefreshCw size={14} className={running ? 'animate-spin' : ''} /> Run checks
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Monitored sources', value: sources.length, icon: Activity, color: 'text-primary-600' },
          { label: 'Healthy sources', value: healthy, icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Open issues', value: issues.length, icon: AlertTriangle, color: 'text-amber-600' },
          { label: 'Events (24h)', value: sources.reduce((sum, s) => sum + s.events_24h, 0), icon: TrendingUp, color: 'text-gray-600' },
        ].map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="card p-4 flex items-center gap-3">
              <Icon size={20} className={card.color} />
              <div>
                <div className="text-xl font-semibold text-gray-900">{card.value}</div>
                <div className="text-xs text-gray-500">{card.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card p-4 mb-4">
        <h2 className="font-medium text-gray-900 mb-3">Open issues</h2>
        {issues.length === 0 ? (
          <p className="text-sm text-gray-400">
            {loading ? 'Loading…' : 'No data quality issue detected on the current scope.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {issues.map(issue => {
              const Icon = TYPE_ICON[issue.type];
              return (
                <li key={issue.id} className={`border rounded-lg px-4 py-3 flex items-start gap-3 ${SEVERITY_STYLE[issue.severity]}`}>
                  <Icon size={16} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{TYPE_LABEL[issue.type]}</span>
                      <span className="badge bg-white/70">{issue.tenant_name}</span>
                      {issue.status === 'acknowledged' && <span className="badge bg-white/70">acknowledged</span>}
                    </div>
                    <p className="text-sm mt-0.5">{issue.message}</p>
                    <p className="text-xs opacity-70 mt-0.5">
                      First seen {new Date(issue.first_seen_at).toLocaleString('en-GB')} · last {new Date(issue.last_seen_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {issue.status !== 'acknowledged' && (
                      <button onClick={() => updateIssue(issue.id, 'acknowledged')} title="Acknowledge"
                        className="p-1.5 rounded-md hover:bg-white/60"><EyeOff size={14} /></button>
                    )}
                    <button onClick={() => updateIssue(issue.id, 'resolved')} title="Mark as resolved"
                      className="p-1.5 rounded-md hover:bg-white/60"><Check size={14} /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card overflow-hidden">
        <h2 className="font-medium text-gray-900 px-4 pt-4 pb-2">Sources</h2>
        <table className="w-full text-sm">
          <thead>
            <tr>
              {shownColumns.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                  {d.label}
                </DraggableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-gray-400" colSpan={9}>Loading…</td></tr>}
            {!loading && sources.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={9}>No active source configured yet</td></tr>
            )}
            {sources.map(s => (
              <tr key={s.id} className="border-t border-gray-100">
                {shownColumns.map(d => (
                  <td key={d.key}
                    className={`table-cell ${d.key === 'tenant' || d.key === 'site' || d.key === 'last_data' ? 'text-gray-500' : ''} ${d.key === 'source' ? 'font-medium text-gray-800' : ''} ${d.key === 'events_24h' ? 'text-gray-800' : ''} ${d.key === 'unparsed_24h' ? (s.unparsed_24h > 0 ? 'text-amber-700' : 'text-gray-500') : ''}`}
                    title={d.key === 'last_data' ? (s.last_data_at ? new Date(s.last_data_at).toLocaleString('en-GB') : '') : undefined}>
                    {d.key === 'status' && (s.open_issues === 0
                      ? <span className="badge bg-green-100 text-green-700">Healthy</span>
                      : <span className="badge bg-amber-100 text-amber-700">{s.open_issues} issue(s)</span>)}
                    {d.key === 'tenant' && s.tenant_name}
                    {d.key === 'source' && s.name}
                    {d.key === 'site' && s.site_name}
                    {d.key === 'broker' && (s.offline_minutes !== null && Number(s.offline_minutes) <= 10
                      ? <span className="badge bg-green-100 text-green-700">Online</span>
                      : <span className="badge bg-red-100 text-red-700">Offline</span>)}
                    {d.key === 'last_data' && formatSilence(s.silent_minutes)}
                    {d.key === 'events_24h' && s.events_24h}
                    {d.key === 'formats' && (s.latest_formats && Object.keys(s.latest_formats).length > 0
                      ? Object.entries(s.latest_formats).map(([format, count]) => (
                          <span key={format} className="badge bg-gray-100 text-gray-600 mr-1">{format} · {count}</span>
                        ))
                      : <span className="text-gray-400">—</span>)}
                    {d.key === 'unparsed_24h' && s.unparsed_24h}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
